import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const groups = [
  {
    city: "Montréal",
    region: "Île-de-Montréal",
    link: "https://facebook.com/groups/495397103978803",
    icon: "ri-building-2-line",
  },
  {
    city: "Québec",
    region: "Capitale-Nationale",
    link: "https://facebook.com/groups/715187445505792",
    icon: "ri-government-line",
  },
  {
    city: "Sherbrooke-Estrie",
    region: "Estrie",
    link: "https://facebook.com/groups/578565813094195",
    icon: "ri-landscape-line",
  },
  {
    city: "Guadeloupe-Beauce",
    region: "Chaudière-Appalaches",
    link: "https://facebook.com/groups/1240018452724954",
    icon: "ri-plant-line",
  },
  {
    city: "Saguenay–Lac-Saint-Jean",
    region: "Saguenay–Lac-Saint-Jean",
    link: "https://facebook.com/groups/1879741595870437",
    icon: "ri-water-flash-line",
  },
];

export default function GroupesBusiness() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = "1";
            (entry.target as HTMLElement).style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-in-up").forEach((el) => {
      (el as HTMLElement).style.opacity = "0";
      (el as HTMLElement).style.transform = "translateY(30px)";
      (el as HTMLElement).style.transition = "opacity 0.7s ease, transform 0.7s ease";
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <Navbar />

      {/* Hero Section */}
      <section className="pt-20 bg-gray-950 relative overflow-hidden min-h-[60vh] flex items-end">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Quebec%20Canada%20city%20skyline%20at%20golden%20hour%2C%20beautiful%20urban%20landscape%20with%20Saint%20Lawrence%20river%2C%20warm%20amber%20and%20dark%20tones%2C%20professional%20photography%2C%20cinematic%20quality%2C%20dramatic%20sky&width=1920&height=800&seq=groupes-hero-001&orientation=landscape"
            alt="Groupes Business"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/40" />
        </div>

        <div className="relative z-10 w-full px-6 pb-16 pt-32">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-500 mb-4">
              Communauté
            </p>
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Ça se passe à<span className="text-amber-500">…</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Photos + Intro */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <div className="fade-in-up rounded-lg overflow-hidden h-64 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=business%20networking%20event%20in%20modern%20conference%20room%2C%20professionals%20shaking%20hands%20and%20discussing%2C%20warm%20professional%20lighting%2C%20diverse%20group%20of%20entrepreneurs%2C%20clean%20modern%20interior%2C%20Quebec%20Canada%20business%20meeting&width=700&height=500&seq=groupes-photo-001&orientation=landscape"
                alt="Groupes Business Networking"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="fade-in-up rounded-lg overflow-hidden h-64 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=entrepreneurs%20collaborating%20around%20table%20with%20laptops%20and%20documents%2C%20startup%20business%20meeting%2C%20modern%20coworking%20space%2C%20natural%20light%2C%20professional%20atmosphere%2C%20teamwork%20and%20innovation%2C%20Quebec%20business%20community&width=700&height=500&seq=groupes-photo-002&orientation=landscape"
                alt="Collaboration Business"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto fade-in-up">
            <p className="text-gray-600 text-base leading-relaxed">
              Rejoignez nos groupes Facebook régionaux et connectez-vous avec des entrepreneurs, des professionnels et des gens d&apos;affaires de votre région.
            </p>
          </div>
        </div>
      </section>

      {/* Groups List */}
      <section className="pb-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto space-y-4">
          {groups.map((group, idx) => (
            <a
              key={group.city}
              href={group.link}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="fade-in-up flex items-center justify-between p-6 rounded-lg border border-gray-100 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/30 transition-all duration-300 group cursor-pointer"
              style={{ transitionDelay: `${idx * 0.08}s` }}
            >
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-gray-100 group-hover:border-amber-200 transition-colors duration-300">
                  <i className={`${group.icon} text-xl text-amber-500`} />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900 group-hover:text-amber-700 transition-colors duration-200">
                    {group.city}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{group.region}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="hidden sm:flex items-center gap-2 text-xs font-medium text-gray-500 bg-white px-4 py-2 rounded-full border border-gray-200 group-hover:border-amber-200 group-hover:text-amber-600 transition-all duration-200">
                  <i className="ri-facebook-circle-fill text-sm" />
                  Rejoindre le groupe
                </span>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-gray-400 group-hover:text-amber-500 group-hover:translate-x-1 transition-all duration-200" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
